import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/axios';
import { getDashboardRoute } from '../utils/routes';
import { STORAGE_KEYS } from '../constants';

const AuthContext = createContext(null);

const DEBUG = import.meta.env.VITE_NODE_ENV !== 'production';

const debugLog = (...args) => {
  if (DEBUG) {
    console.log('[Auth Debug]', ...args);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // List of public routes that don't require authentication
  const publicRoutes = ['/', '/about', '/contact', '/signin', '/signup'];

  // Function to validate token format
  const isValidToken = (token) => {
    if (!token) return false;
    try {
      // Check if token is a valid JWT format (xxx.yyy.zzz)
      const parts = token.split('.');
      return parts.length === 3 && parts.every(part => part.length > 0);
    } catch {
      return false;
    }
  };

  useEffect(() => {
    let isMounted = true;
    const initAuth = async () => {
      // Don't initialize auth if we're currently logging in or component unmounted
      if (isLoggingIn || !isMounted) {
        debugLog('Skipping auth initialization - login in progress or unmounted');
        return;
      }
      
      debugLog('Initializing auth state');
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const storedUser = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      
      debugLog('Initial auth check:', {
        token: token ? 'exists' : 'null',
        tokenValid: isValidToken(token),
        storedUser: storedUser ? JSON.parse(storedUser) : 'null',
        currentPath: location.pathname
      });
      
      if (token && isValidToken(token)) {
        // If we have stored user data, use it immediately to avoid unnecessary API calls
        if (storedUser && !user) {
          try {
            const userData = JSON.parse(storedUser);
            debugLog('Using stored user data:', userData);
            if (isMounted) {
              setUser(userData);
              setLoading(false);
              
              // Only redirect to dashboard if we're on the signin/signup page
              if ((location.pathname === '/signin' || location.pathname === '/signup') && !isLoggingIn) {
                const dashboardRoute = getDashboardRoute(userData.role);
                debugLog('Redirecting to dashboard:', dashboardRoute);
                navigate(dashboardRoute);
              }
            }
            return;
          } catch (err) {
            debugLog('Error parsing stored user:', err);
          }
        }
        
        // Only validate token with server if we don't have user data
        if (!user) {
          try {
            debugLog('Token found, validating with server');
            
            // Use single auth endpoint
            debugLog('Validating token with server');
            const response = await api.get('/auth/me');
            
            debugLog('Server response:', response.data);
            
            if (response.data.status === 'success') {
              // Handle both response structures (direct user or nested in data)
              const userData = response.data.user || response.data.data?.user;
              
              if (userData) {
                debugLog('Setting user state:', userData);
                if (DEBUG) {
                  console.log('🔄 Auth initialization - User data:', {
                    id: userData._id,
                    email: userData.email,
                    role: userData.role,
                    name: userData.name
                  });
                }
                if (isMounted) {
                  setUser(userData);
                  
                  // Only redirect to dashboard if we're on the signin/signup page AND not currently logging in
                  if ((location.pathname === '/signin' || location.pathname === '/signup') && !isLoggingIn) {
                    const dashboardRoute = getDashboardRoute(userData.role);
                    debugLog('Redirecting to dashboard:', dashboardRoute);
                    navigate(dashboardRoute);
                  }
                }
              } else {
                debugLog('No user data in response:', response.data);
                handleLogout('Invalid user data');
              }
            } else {
              debugLog('Invalid server response:', response.data);
              handleLogout('Invalid server response');
            }
          } catch (error) {
            debugLog('Auth check error:', error.response || error);
            handleLogout('Token validation failed');
          }
        }
      } else {
        debugLog('No valid token found');
        // Only redirect to signin if we're not on a public route
        const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));
        if (!isPublicRoute && !location.pathname.includes('/signin') && isMounted) {
          debugLog('Redirecting to signin from:', location.pathname);
          navigate('/signin', { state: { from: location.pathname } });
        }
      }
      if (isMounted) {
        setLoading(false);
      }
    };

    initAuth();
    
    return () => {
      isMounted = false;
    };
  }, [navigate, location.pathname, isLoggingIn]);

  const handleLogout = (reason = '') => {
    debugLog('Logging out', reason);
    setUser(null);
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  };

  const login = async (email, password) => {
    setIsLoggingIn(true);
    try {
      debugLog('Attempting login for:', email);
      
      // Use single auth endpoint
      const response = await api.post('/auth/login', { email, password });
      
      debugLog('Login response:', response.data);
      
      console.log('🔍 DEBUG - Full response:', {
        status: response.data.status,
        hasToken: !!response.data.token,
        tokenType: typeof response.data.token,
        tokenValue: response.data.token,
        tokenLength: response.data.token?.length,
        hasData: !!response.data.data,
        hasUser: !!response.data.data?.user
      });
      
      if (response.data.status === 'success' && response.data.token) {
        const { token } = response.data;
        const userData = response.data.data.user;
        
        debugLog('Validating token:', {
          token: token.substring(0, 20) + '...',
          parts: token.split('.').map(part => part.length),
          valid: isValidToken(token)
        });
        
        if (!isValidToken(token)) {
          throw new Error('Received invalid token format from server');
        }

        // Set auth state
        debugLog('Setting auth state:', { 
          userData, 
          token: token.substring(0, 10) + '...' // Only log part of the token
        });
        
        if (DEBUG) {
          console.log('🔑 Login successful - User data:', {
            id: userData._id,
            email: userData.email,
            role: userData.role,
            name: userData.name
          });
        }
        
        setUser(userData);
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
        
        toast.success('Logged in successfully!');
        
        // Navigate to the appropriate dashboard or the previous attempted route
        const redirectAfterLogin = localStorage.getItem('redirectAfterLogin');
        const intendedPath = redirectAfterLogin || location.state?.from;
        const dashboardRoute = getDashboardRoute(userData.role);
        
        // Clear the redirect URL after using it
        if (redirectAfterLogin) {
          localStorage.removeItem('redirectAfterLogin');
        }
        
        debugLog('Navigating to:', intendedPath || dashboardRoute);
        navigate(intendedPath || dashboardRoute);
        
        return userData;
      } else {
        // Handle cases where status might be 'success' but no token is provided
        const errorMessage = response.data.message || 'Invalid credentials';
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      debugLog('Login error:', error);
      const message = error.response?.data?.message || error.message || 'Login failed';
      toast.error(message);
      throw error;
    } finally {
      setIsLoggingIn(false);
    }
  };

  const signup = async (formData) => {
    try {
      debugLog('Attempting signup:', formData);
      
      // If signing up as a worker, get a proper worker ID from the server
      let workerId = null;
      if (formData.role === 'worker') {
        try {
          // First try to get a worker ID directly from the server
          if (DEBUG) console.log('Requesting worker ID from server...');
          const response = await api.get('/users/next-employee-id');
          
          if (response.data?.employeeId) {
            workerId = response.data.employeeId;
            if (DEBUG) console.log('✅ Received worker ID from server:', workerId);
          } else if (response.data?.nextId) {
            // Format the ID ourselves if we only got a number
            const year = new Date().getFullYear().toString().slice(-2);
            workerId = `WRK-${year}-${response.data.nextId.toString().padStart(4, '0')}`;
            if (DEBUG) console.log('✅ Formatted worker ID from counter:', workerId);
          } else {
            throw new Error('Invalid response format from server');
          }
        } catch (error) {
          if (DEBUG) console.error('❌ Failed to get worker ID from server:', error);
          // Generate a fallback ID
          const year = new Date().getFullYear().toString().slice(-2);
          const randomId = Math.floor(1000 + Math.random() * 9000);
          workerId = `WRK-${year}-${randomId}`;
          if (DEBUG) console.log('⚠️ Using fallback worker ID:', workerId);
        }
      }
      
      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role || 'user',
        phone: formData.phone,
        address: formData.address,
        zone: formData.zone,
        // Always set worker status to pending
        status: formData.role === 'worker' ? 'pending' : 'active',
        // Include worker ID if we got one
        ...(workerId ? { workerId } : {})
      };

      // Add explicit workerDetails with status for workers
      if (formData.role === 'worker') {
        userData.workerDetails = {
          status: 'pending',
          employeeId: workerId
        };
      }

      debugLog('Sending signup data to server:', {...userData, password: '[REDACTED]'});
      
      // Use single auth endpoint
      const response = await api.post('/auth/register', userData);
      
      debugLog('Signup response:', response.data);

      if (response.data.status === 'success') {
        handleLogout('New signup');
        
        // Show specific message for worker signups
        if (formData.role === 'worker') {
          toast.success('Worker account created! Your application is pending approval.');
        } else {
          toast.success('Account created successfully! Please sign in to continue.');
        }
        
        sessionStorage.setItem('lastSignupEmail', userData.email);
        navigate('/signin');
        return response.data.data.user;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      debugLog('Signup error:', error.response || error);
      const message = error.response?.data?.message || 'Failed to create account';
      toast.error(message);
      throw error;
    }
  };

  const logout = async () => {
    setIsLoggingOut(true);
    try {
      debugLog('Manual logout initiated');
      handleLogout('User initiated');
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      debugLog('Logout error:', error);
      toast.error('Failed to logout');
    } finally {
      // Reset after a short delay to allow navigation to complete
      setTimeout(() => {
        setIsLoggingOut(false);
      }, 500);
    }
  };

  const updateUser = (updatedUserData) => {
    debugLog('Updating user data:', updatedUserData);
    setUser(updatedUserData);
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUserData));
  };

  const value = {
    user,
    loading,
    login,
    logout,
    signup,
    updateUser,
    isAuthenticated: !!user,
    isLoggingOut,
    role: user?.role,
  };

  debugLog('Auth state update:', {
    isAuthenticated: !!user,
    role: user?.role,
    loading,
    currentPath: location.pathname
  });

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 